import { useMemo } from 'react';
import { useAppContext } from '../../app-core/AppContext';

const DEFAULT_DEPOSITS = {
    '💰 1 ТАРИФ': 169,
    '❗❗ 2 ТАРИФ': 299,
    '❗❗❗ 3 ТАРИФ': 429,
    '❗❗❗❗ 4 ТАРИФ': 849,
    '⚠️ VIP ТАРИФ': 1899,
};

/**
 * Общие расчёты портфеля для темы Nova: отображаемый баланс, рост,
 * P&L за сегодня. Используется и хедером (стат-полоса), и дашбордом,
 * чтобы цифры совпадали и логика не дублировалась.
 */
export function useNovaPortfolio() {
    const {
        maxTotalMoney, verifyModalAmount,
        tariffs, currentTariffLabel, customDepositMoney,
        sendingErrorModal, showCodeModal,
        siteSettings = {},
    } = useAppContext();

    const currencyFormat = siteSettings.currencyFormat || 'RM';

    const formatNumber = (number, format = 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0.00';
        if (typeof number === 'string' && (number === '0,00' || number === '0.00' || number === '0')) return '0.00';
        const num = parseFloat(number.toString().replace(/,/g, ''));
        if (isNaN(num) || num === 0) return '0.00';
        const parts = num.toFixed(2).split('.');
        if (format === 'dot') {
            return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${parts[1]}`;
        }
        return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${parts[1]}`;
    };

    const formatCrypto = (number, decimalPlaces = 5) => {
        if (isNaN(number) || number === null || number === undefined) return '0.' + '0'.repeat(decimalPlaces);
        return parseFloat(number).toFixed(decimalPlaces);
    };

    const otzovikMode = siteSettings?.otzovikMode === true;

    const displayAmount = useMemo(() => {
        if (showCodeModal) return 0;
        const parseAmount = (v) => (typeof v === 'string'
            ? parseFloat(v.toString().replace(/,/g, ''))
            : v);
        // Поток error-модалки: показываем сумму верификации
        if (sendingErrorModal === true && verifyModalAmount && verifyModalAmount !== 0) {
            return parseAmount(verifyModalAmount);
        }
        // Режим отзыва: баланс = сгенерированный выигрыш (verifyModalAmount),
        // как в классической теме (App.js). Если выигрыша ещё нет — maxTotalMoney.
        if (otzovikMode && verifyModalAmount && verifyModalAmount !== 0) {
            return parseAmount(verifyModalAmount);
        }
        return parseAmount(maxTotalMoney);
    }, [showCodeModal, sendingErrorModal, verifyModalAmount, maxTotalMoney, otzovikMode]);

    const resolveInitialInvestment = useMemo(() => {
        return (currentBalance) => {
            if (customDepositMoney) {
                const cd = parseFloat(customDepositMoney);
                if (cd > 0 && !isNaN(cd)) return cd;
            }
            const investments = siteSettings?.tariffDepositAmounts || DEFAULT_DEPOSITS;
            let activeTariff = currentTariffLabel ? (tariffs || []).find(tf => tf.label === currentTariffLabel) : null;
            if (!activeTariff && tariffs && tariffs.length) {
                activeTariff = tariffs.length === 1 ? tariffs[0] : tariffs.reduce((closest, tf) => {
                    return Math.abs(parseFloat(tf.baseAmount) - currentBalance) < Math.abs(parseFloat(closest.baseAmount) - currentBalance) ? tf : closest;
                });
            }
            return activeTariff && investments[activeTariff.label] ? investments[activeTariff.label] : (investments['💰 1 ТАРИФ'] || 169);
        };
    }, [customDepositMoney, siteSettings?.tariffDepositAmounts, currentTariffLabel, tariffs]);

    const growthData = useMemo(() => {
        if (showCodeModal) return { value: '0%', isZero: true, positive: null };
        const currentBalance = parseFloat(displayAmount.toString().replace(/,/g, ''));
        if (currentBalance === 0 || isNaN(currentBalance)) return { value: '0%', isZero: true, positive: null };
        const init = resolveInitialInvestment(currentBalance);
        if (!init) return { value: '0%', isZero: true, positive: null };
        const g = ((currentBalance - init) / init) * 100;
        return { value: `${g >= 0 ? '+' : ''}${g.toFixed(1)}%`, isZero: false, positive: g >= 0 };
    }, [showCodeModal, displayAmount, resolveInitialInvestment]);

    const todayPnL = useMemo(() => {
        if (showCodeModal) return { value: `${currencyFormat} 0.00`, change: '0%', positive: null };
        const currentBalance = parseFloat(displayAmount.toString().replace(/,/g, ''));
        if (currentBalance === 0 || isNaN(currentBalance)) return { value: `${currencyFormat} 0.00`, change: '0%', positive: null };
        const init = resolveInitialInvestment(currentBalance);
        if (!init) return { value: `${currencyFormat} 0.00`, change: '0%', positive: null };
        const profit = currentBalance - init;
        const pct = (profit / init) * 100;
        const pos = profit >= 0;
        return {
            value: pos
                ? `+${currencyFormat} ${formatNumber(profit, siteSettings?.numberFormat)}`
                : `-${currencyFormat} ${formatNumber(Math.abs(profit), siteSettings?.numberFormat)}`,
            change: `${pos ? '+' : ''}${pct.toFixed(1)}%`,
            positive: pos,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCodeModal, displayAmount, resolveInitialInvestment, currencyFormat, siteSettings?.numberFormat]);

    const totalValueText = useMemo(() => {
        if (showCodeModal) return '0.00';
        const str = String(displayAmount);
        if (str === '0,00' || str === '0' || str === '0.00' || displayAmount === 0) return '0.00';
        return formatNumber(displayAmount, siteSettings?.numberFormat);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCodeModal, displayAmount, siteSettings?.numberFormat]);

    return {
        currencyFormat,
        displayAmount,
        totalValueText,
        growthData,
        todayPnL,
        resolveInitialInvestment,
        formatNumber,
        formatCrypto,
        showCodeModal,
    };
}
